import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Download,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Info,
  Target,
  Zap,
} from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const COBITReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [selectedDomain, setSelectedDomain] = useState("all");
  const navigate = useNavigate();
  const location = useLocation();
  const reportRef = useRef(null);

  // Toast notification
  useEffect(() => {
    if (location.state?.showSuccess) {
      toast.success("🎉 Your COBIT assessment report is now available.", { autoClose: 5000 });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch report data
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      navigate("/login");
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}maturity/report/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setReportData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (err.response?.status === 401) {
          navigate("/login");
        }
        setError("Failed to load COBIT report. Please try again.");
        setLoading(false);
      });
  }, [navigate]);

  // Helper: wait for paint (double requestAnimationFrame + delay)
  const waitForPaint = () => {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 150);
        });
      });
    });
  };

  // Data
  const allDomains = reportData?.domains || [];

  // Filtered domains: exclude drafts, then filter by selectedDomain
  const submittedDomains = allDomains;
  const filteredDomains =
    selectedDomain === "all"
      ? submittedDomains
      : submittedDomains.filter((d) => d.domain === selectedDomain);

  // Calculations based on submitted and filtered domains
  const totalAssessmentItems = filteredDomains.reduce(
    (s, d) => s + (d.total_controls || 0),
    0
  );
  const totalAchievedItems = filteredDomains.reduce(
    (s, d) => s + (d.compliant_controls || 0),
    0
  );
  const overallAchievementPercent =
    totalAssessmentItems === 0 ? 0 : (totalAchievedItems / totalAssessmentItems) * 100;
  const totalGaps = filteredDomains.reduce((sum, d) => {
    return sum + (d.practices?.reduce((pSum, p) => {
      return pSum + (p.questions?.reduce((qSum, q) => {
        return qSum + (q.gaps?.length || 0);
      }, 0) || 0);
    }, 0) || 0);
  }, 0);
  const submittedDomainsCount = filteredDomains.length; // by definition all are submitted

  const getCapabilityLevelNumber = (level) => {
    if (level === null || level === undefined) return 0;
  
    if (typeof level === "number") {
      return level;
    }
  
    if (typeof level === "string") {
      const match = level.match(/^\d+/);
      return match ? parseInt(match[0], 10) : 0;
    }
  
    return 0;
  };
  
  const levelStats = {
    incomplete: filteredDomains.filter(
      d => getCapabilityLevelNumber(d.current_level) === 0
    ).length,
  
    initial: filteredDomains.filter(
      d => getCapabilityLevelNumber(d.current_level) === 1
    ).length,
  
    managed: filteredDomains.filter(
      d => getCapabilityLevelNumber(d.current_level) === 2
    ).length,
  
    defined: filteredDomains.filter(
      d => getCapabilityLevelNumber(d.current_level) === 3
    ).length,
  };

  const averageCurrentLevel =
    filteredDomains.length > 0
      ? filteredDomains.reduce((sum, d) => {
          const level = typeof d.current_level === "string"
            ? parseInt(d.current_level.split(" - ")[0], 10)
            : d.current_level;
          return sum + (level || 0);
        }, 0) / filteredDomains.length
      : 0;

  const highPriorityGaps = filteredDomains.reduce((total, d) => {
    return total + (d.practices?.reduce((pSum, p) => {
      return pSum + (p.questions?.reduce((qSum, q) => {
        return qSum + (q.gaps?.filter(g => g.weight > 7)?.length || 0);
      }, 0) || 0);
    }, 0) || 0);
  }, 0);

  const lowPriorityGaps = totalGaps - highPriorityGaps;

  const lowestAchievementDomains = filteredDomains
    .map(d => ({
      domain: d.domain,
      achievement: d.total_controls === 0 ? 0 : (d.compliant_controls / d.total_controls) * 100
    }))
    .sort((a, b) => a.achievement - b.achievement)
    .slice(0, 3);

  const highestAchievementDomains = filteredDomains
    .map(d => ({
      domain: d.domain,
      achievement: d.total_controls === 0 ? 0 : (d.compliant_controls / d.total_controls) * 100
    }))
    .sort((a, b) => b.achievement - a.achievement)
    .slice(0, 3);

  const getHealthStatus = () => {
    if (averageCurrentLevel >= 2.5) {
      return {
        status: "Strong",
        color: "bg-green-50 border-green-300",
        textColor: "text-green-700",
        icon: "text-green-600",
      };
    }
    if (averageCurrentLevel >= 1.5) {
      return {
        status: "Developing",
        color: "bg-yellow-50 border-yellow-300",
        textColor: "text-yellow-700",
        icon: "text-yellow-600",
      };
    }
    return {
      status: "Needs Improvement",
      color: "bg-red-50 border-red-300",
      textColor: "text-red-700",
      icon: "text-red-600",
    };
  };

  const healthStatus = getHealthStatus();

  const toggleDomainExpand = (idx) => {
    setExpandedDomains(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const getLevelLabel = (level) => {
    if (level === null || level === undefined) return "0 - Incomplete / Not Performed";
    const num = typeof level === "string" ? parseInt(level.split(" - ")[0], 10) : level;
    switch (num) {
      case 0: return "0 - Incomplete / Not Performed";
      case 1: return "1 - Initial";
      case 2: return "2 - Managed";
      case 3: return "3 - Defined";
      default: return "0 - Incomplete / Not Performed";
    }
  };

  const getLevelColor = (level) => {
    const num = typeof level === "string" ? parseInt(level.split(" - ")[0], 10) : level;
    switch (num) {
      case 0: return "bg-gray-100 text-gray-800 border-gray-300";
      case 1: return "bg-red-100 text-red-800 border-red-300";
      case 2: return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case 3: return "bg-green-100 text-green-800 border-green-300";
      default: return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  // Export PDF
  const handleExportPDF = async () => {
    if (selectedDomain === "all") {
      toast.warning("Please select only one process to export. Exporting all processes may produce a very large PDF.");
      return;
    }

    const domainIdx = (() => {
      const idx = filteredDomains.findIndex(d => d.domain === selectedDomain);
      return idx >= 0 ? idx : -1;
    })();

    if (domainIdx === -1) {
      toast.error("Selected process not found. Please select a valid process.");
      return;
    }

    setExporting(true);

    try {
      // Expand only the selected domain
      const allDomainIndices = {};
      if (domainIdx >= 0) {
        allDomainIndices[domainIdx] = true;
      }
      setExpandedDomains(allDomainIndices);
      await waitForPaint(); // wait for paint before canvas capture

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      if (canvas.width === 0 || canvas.height === 0) throw new Error("Canvas has zero dimension");

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const startY = 25;
      const usablePageHeight = pageHeight - startY - margin;

      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(255, 77, 0);
      pdf.text("COBIT Capability Assessment Report", pageWidth / 2, 15, { align: "center" });
      // Date
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, 22, { align: "center" });

      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= usablePageHeight) {
        pdf.addImage(imgData, "PNG", margin, startY, imgWidth, imgHeight);
      } else {
        let yOffset = 0;
        const totalHeight = canvas.height;
        const pageHeightPx = (usablePageHeight * canvas.width) / imgWidth;
        while (yOffset < totalHeight) {
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.min(canvas.height - yOffset, pageHeightPx);
          const ctx = sliceCanvas.getContext("2d");
          ctx.drawImage(
            canvas,
            0,
            yOffset,
            canvas.width,
            sliceCanvas.height,
            0,
            0,
            sliceCanvas.width,
            sliceCanvas.height
          );
          const pageImage = sliceCanvas.toDataURL("image/png");
          const displayHeight = (sliceCanvas.height * imgWidth) / canvas.width;
          if (yOffset > 0) pdf.addPage();
          pdf.addImage(pageImage, "PNG", margin, startY, imgWidth, displayHeight);
          yOffset += sliceCanvas.height;
        }
      }

      // Footer pages
      const pageCount = pdf.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      }

      pdf.save(`cobit-report-${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("✅ Report exported successfully!");
      setExporting(false);
      setExpandedDomains({});
    } catch (err) {
      console.error(err);
      toast.error("❌ Failed to export report");
      setExporting(false);
      setExpandedDomains({});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-orange-700 font-medium">Loading COBIT Report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button
            onClick={() => navigate("/cobit/dashboard")}
            className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Chart data
  const chartData = filteredDomains
    .sort((a, b) => {
      const numA = parseInt(a.domain?.replace(/\D/g, "") || 0, 10);
      const numB = parseInt(b.domain?.replace(/\D/g, "") || 0, 10);
      return numA - numB;
    })
    .map(domain => ({
      domain: domain.domain || "Process",
      fullName: domain.domain_title || "Unknown Objective",
      achievement: parseFloat((domain.compliance_percent || 0).toFixed(2)),
      total: domain.total_controls || 0,
      achieved: domain.compliant_controls || 0,
    }));

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-8 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/cobit/dashboard")}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium transition"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
        </div>

        {/* Warning note about one domain */}
        <div className="mb-4 p-4 bg-yellow-100 border border-yellow-300 rounded-lg text-yellow-900 font-semibold">
          ⚠️ To ensure reliable PDF generation, please select only one process. Exporting multiple processes may produce a very large PDF or result in an incomplete report.
        </div>

        {/* Domain select (single domain) */}
        <div className="mb-4 flex items-center space-x-4">
          <label className="font-semibold text-gray-700">Select Process:</label>
          <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
          >
              <option value="all">All Submitted Processes</option>

              {submittedDomains.map((d) => (
                  <option key={d.domain} value={d.domain}>
                      {d.domain} - {d.domain_title}
                  </option>
              ))}
          </select>
        </div>

        {/* Show info if no submitted domains */}
        {submittedDomains.length === 0 && (
          <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
            No submitted assessments available for this selection.
          </div>
        )}

        {/* Show warning about draft exclusions */}
        {allDomains.some(d => d.status === "draft") && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg text-yellow-900 font-semibold">
            {allDomains.filter(d => d.status === "draft").length} process(es) still in draft are excluded from this report.
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Average Capability Level</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">{averageCurrentLevel.toFixed(1)}</p>
                <p className="text-sm text-gray-500 mt-2">Scale: 0–3</p>
              </div>
              <TrendingUp className="text-orange-600" size={32} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Assessment Items</p>
            <p className="text-3xl font-bold text-blue-600 mt-2">{totalAssessmentItems}</p>
            <p className="text-sm text-green-600 mt-2">✓ {totalAchievedItems} Achieved</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Processes Assessed</p>
            <p className="text-3xl font-bold text-purple-600 mt-2">{submittedDomainsCount}/{submittedDomains.length}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Total Gaps Identified</p>
            <p className="text-3xl font-bold text-red-600 mt-2">{totalGaps}</p>
          </div>
        </div>

        {/* EXECUTIVE SUMMARY */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8 border border-gray-200">
          <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-6 text-white">
            <div className="flex items-center gap-3">
              <Info size={28} />
              <h2 className="text-2xl font-bold">Executive Summary</h2>
            </div>
            <p className="text-orange-100 text-sm mt-2">
              High-level overview of your COBIT process capability assessment.
            </p>
          </div>

          <div className="p-8">
            {/* Health Banner */}
            <div className={`${healthStatus.color} border-2 rounded-lg p-6 mb-8`}>
              <div className="flex items-start gap-4">
                <div className={`${healthStatus.icon} mt-1`}>
                  <Zap size={24} />
                </div>
                <div className="flex-1">
                  <h3 className={`text-xl font-bold ${healthStatus.textColor} mb-2`}>
                    Overall Capability Status: {healthStatus.status}
                  </h3>
                  <p className="text-gray-700">
                    Your organization’s COBIT capability maturity is currently averaging{" "}
                    <span className="font-semibold">{averageCurrentLevel.toFixed(1)}</span> on the 0–3 capability scale.
                    {averageCurrentLevel >= 2.5 && " Assessed COBIT processes demonstrate a strong level of capability maturity."}
                    {averageCurrentLevel >= 1.5 && averageCurrentLevel < 2.5 && " Several assessed processes are progressing, but further improvement is needed to strengthen capability maturity."}
                    {averageCurrentLevel < 1.5 && " Significant improvement is needed to strengthen governance and management capability maturity across assessed processes."}
                  </p>
                </div>
              </div>
            </div>

            {/* Key Findings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Current State */}
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold">📊</span>
                  Current Capability State
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 0 (Incomplete)</span>
                    <span className="font-bold text-gray-900">{levelStats.incomplete}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 1 (Initial)</span>
                    <span className="font-bold text-gray-900">{levelStats.initial}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 2 (Managed)</span>
                    <span className="font-bold text-gray-900">{levelStats.managed}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 3 (Defined)</span>
                    <span className="font-bold text-gray-900">{levelStats.defined}</span>
                  </div>
                </div>
              </div>

              {/* Secondary Assessment View */}
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Target size={20} className="text-orange-600" />
                  Coverage & Gap Analysis
                </h4>
                <div className="space-y-3">
                  <div className="p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm text-gray-600 mb-1">Average Current Capability Level</p>
                    <p className="text-2xl font-bold text-orange-600">{averageCurrentLevel.toFixed(1)}</p>
                  </div>
                  <div className="p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm text-gray-600 mb-1">Assessment Achievement</p>
                    <p className="text-2xl font-bold text-blue-600">{overallAchievementPercent.toFixed(1)}%</p>
                  </div>
                  <div className="p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm text-gray-600 mb-1">Total Gaps Identified</p>
                    <p className="text-2xl font-bold text-red-600">{totalGaps}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-red-50 rounded border border-red-200">
                      <p className="text-xs text-red-700 mb-1">High Priority</p>
                      <p className="text-xl font-bold text-red-600">{highPriorityGaps}</p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
                      <p className="text-xs text-yellow-700 mb-1">Low Priority</p>
                      <p className="text-xl font-bold text-yellow-600">{lowPriorityGaps}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Insights */}
            <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 mb-8">
              <h4 className="font-bold text-gray-900 mb-4 text-lg">Key Insights</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded border-l-4 border-l-orange-600">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Assessment Coverage:</span> {submittedDomainsCount} out of {submittedDomains.length} objectives have been assessed ({submittedDomains.length > 0 ? Math.round((submittedDomainsCount / submittedDomains.length) * 100) : 0}% complete).
                  </p>
                </div>
                {lowestAchievementDomains.length > 0 && (
                  <div className="p-3 bg-white rounded border-l-4 border-l-red-600">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Processes Needing Attention:</span> {lowestAchievementDomains.map(d => d.domain).join(", ")} need the most improvement based on lower achievement across assessment items.
                    </p>
                  </div>
                )}
                {highestAchievementDomains.length > 0 && (
                  <div className="p-3 bg-white rounded border-l-4 border-l-green-600">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Stronger Processes:</span> {highestAchievementDomains.map(d => d.domain).join(", ")} are showing stronger performance across assessed items.
                    </p>
                  </div>
                )}
                <div className="p-3 bg-white rounded border-l-4 border-l-orange-600">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Assessment Results:</span> {totalAchievedItems} out of {totalAssessmentItems} assessment items are currently achieved, while {totalAssessmentItems - totalAchievedItems} items require further improvement.
                  </p>
                </div>
              </div>
            </div>

            {/* What's in this report */}
            <div className="bg-orange-50 border border-orange-300 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                <Info size={20} className="text-orange-600" />
                What You'll Find in This Report
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-orange-600 font-bold">1.</span>
                  <p><span className="font-semibold">Process Capability Overview Chart</span> - Visual representation of assessment achievement across COBIT governance and management objectives</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-orange-600 font-bold">2.</span>
                  <p><span className="font-semibold">Detailed Process Breakdown</span> - Metrics for each process including current capability level and assessment items</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-orange-600 font-bold">3.</span>
                  <p><span className="font-semibold">Gap Analysis</span> - Identified gaps with recommendations and priority weights for each issue</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-orange-600 font-bold">4.</span>
                  <p><span className="font-semibold">Capability Metrics</span> - Summary tables showing capability levels, assessment items, and target state</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main report section */}
        <div ref={reportRef} className="bg-white rounded-xl shadow-lg overflow-hidden">

          {/* Header inside report */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-8 text-white">
            <h2 className="text-3xl font-bold mb-2">COBIT Capability Assessment Report</h2>
            <p className="text-orange-100">Comprehensive assessment of governance and management objectives and capability levels</p>
            <p className="text-orange-100 text-sm mt-4">Generated on {new Date().toLocaleDateString()}</p>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Process Capability Overview</h3>
              <div className="bg-gray-50 p-6 rounded-lg">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="domain"
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      label={{ value: "Achievement %", angle: -90, position: "insideLeft" }}
                    />
                    <Tooltip
                      formatter={(value) => [`${value}%`, "Achievement"]}
                      labelFormatter={(label) => {
                        const data = chartData.find(d => d.domain === label);
                        return data ? `${data.domain}: ${data.fullName}` : label;
                      }}
                      contentStyle={{
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        padding: "12px",
                        fontSize: "13px"
                      }}
                    />
                    <Bar dataKey="achievement" fill="#FF4D00" radius={[8, 8, 0, 0]}>
                      <LabelList
                        dataKey="achievement"
                        position="insideTop"
                        formatter={(value) => `${value}%`}
                        style={{ fill: "white", fontSize: 12, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {chartData.map((domain, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-bold text-orange-600">{domain.domain}</p>
                    <p className="text-xs text-gray-600 mt-1 leading-tight">{domain.fullName}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Process details */}
          <div className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Process Details</h3>

            {submittedDomains.length > 0 ? (
              filteredDomains.map((domain, idx) => {
                const total = domain.total_controls || 0;
                const achieved = domain.compliant_controls || 0;
                const achievementPercent = total === 0 ? 0 : (achieved / total) * 100;
                const isExpanded = expandedDomains[idx];

                return (
                  <div key={idx} className="mb-6 border border-gray-200 rounded-lg overflow-hidden last:mb-0">
                    {/* Header */}
                    <button
                      onClick={() => toggleDomainExpand(idx)}
                      className="w-full bg-gray-50 hover:bg-gray-100 transition p-6 flex items-center justify-between group"
                    >
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-4 flex-wrap">
                          <h4 className="text-xl font-bold text-gray-900">
                            {domain.domain} - {domain.domain_title}
                          </h4>
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getLevelColor(domain.current_level)}`}>
                            {getLevelLabel(domain.current_level)}
                          </span>
                        </div>
                        <div className="flex gap-6 mt-4 flex-wrap">
                          <div>
                            <p className="text-sm text-gray-600">Assessment Achievement</p>
                            <p className="text-lg font-bold text-orange-600">{achievementPercent.toFixed(1)}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Assessment Items</p>
                            <p className="text-lg font-bold text-gray-900">{achieved}/{total}</p>
                          </div>
                        </div>
                      </div>
                      {/* Button */}
                      <div className="flex-shrink-0 ml-6">
                        <span className="px-4 py-2 bg-orange-600 group-hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition whitespace-nowrap">
                          {isExpanded ? "Hide Details" : "Extract Details"}
                        </span>
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="p-6 border-t border-gray-200 bg-white">
                        {/* No draft branch removed because drafts are excluded */}
                        {/* Summary table */}
                        <div className="overflow-x-auto mb-6">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-orange-50 border-y border-orange-200">
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Metric</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-700">Assessment Items</td>
                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">{domain.total_controls}</td>
                              </tr>
                              <tr className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-700">Achieved Items</td>
                                <td className="px-4 py-3 text-sm font-semibold text-green-600">{domain.compliant_controls}</td>
                              </tr>
                              <tr className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-700">Assessment Achievement</td>
                                <td className="px-4 py-3 text-sm font-semibold text-orange-600">{achievementPercent.toFixed(2)}%</td>
                              </tr>
                              <tr className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-700">Current Capability Level</td>
                                <td className="px-4 py-3">
                                  <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full border ${getLevelColor(domain.current_level)}`}>
                                    {getLevelLabel(domain.current_level)}
                                  </span>
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-700">Target Capability Level</td>
                                <td className="px-4 py-3">
                                  <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border bg-green-100 text-green-800 border-green-300">
                                    3 - Defined
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Practices & Gaps */}
                        {domain.practices && domain.practices.length > 0 && (
                          <div>
                            <h5 className="font-bold text-gray-900 mb-4 text-lg">Practices & Gaps</h5>
                            {domain.practices
                              .filter(p => p.questions?.some(q => q.gaps?.length > 0))
                              .map((p, pidx) => (
                                <div key={pidx} className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                                  <h6 className="font-semibold text-gray-900 mb-4">
                                    {p.practice} - {p.practice_title}
                                  </h6>
                                  <div className="space-y-4">
                                    {p.questions
                                      .filter(q => q.gaps?.length > 0)
                                      .map((q, qidx) => (
                                        <div key={qidx} className="bg-white p-4 rounded border border-gray-200">
                                          <div className="flex items-start gap-3 mb-3">
                                            <AlertCircle size={18} className="text-orange-600 flex-shrink-0 mt-0.5" />
                                            <p className="font-medium text-gray-900">{q.request}</p>
                                          </div>
                                          <div className="ml-6 space-y-3">
                                            {q.gaps.map((gap, gidx) => (
                                              <div key={gidx} className="p-3 bg-red-50 rounded border border-red-200">
                                                <p className="text-sm font-semibold text-red-900 mb-2">Gap {gidx + 1}</p>
                                                <p className="text-sm text-gray-700 mb-2">
                                                  <span className="font-semibold">Issue:</span> {gap.gap}
                                                </p>
                                                <p className="text-sm text-gray-700 mb-2">
                                                  <span className="font-semibold">Recommendation:</span> {gap.recommendation}
                                                </p>
                                                {gap.weight && (
                                                  <p className="text-sm">
                                                    <span className="font-semibold">Priority Weight:</span>{" "}
                                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                                                      gap.weight > 7 ? "bg-red-100 text-red-800" :
                                                      gap.weight > 4 ? "bg-yellow-100 text-yellow-800" :
                                                      "bg-green-100 text-green-800"
                                                    }`}>
                                                      {gap.weight}/10
                                                    </span>
                                                  </p>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}

                        {/* No gaps message */}
                        {!domain.practices?.some(p => p.questions?.some(q => q.gaps?.length > 0)) && (
                          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg text-green-700 border border-green-200">
                            <CheckCircle2 size={20} />
                            <p className="font-medium">No gaps identified. This objective is performing well based on the current assessment.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 mb-4">
                No submitted assessments available for this selection.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-8 border-t border-gray-200 text-center text-sm text-gray-600">
            <p>This report was automatically generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
            <p className="mt-2">Primary view: COBIT capability level (0–3). Secondary view: assessment coverage, achieved items, and identified gaps.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center sm:justify-start">
          <button
            onClick={() => navigate("/cobit/dashboard")}
            className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition shadow-md hover:shadow-lg"
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={18} />
            {exporting ? "Generating PDF..." : "Export as PDF"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default COBITReport;